import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { useFetchWrapper as Api } from '../../utils';
import { RESELLER_FILTER } from '../../components/constants/api-paths';
import { SELECTED_TIME_ZONE } from '../../components/constants/constants';
import { customSort } from '../../utils/helper';

const PAGE_SIZE = 100;

// Fetches every reseller (paginating until total_count is covered) and builds
// the flat legalName option list + legalName -> referralCode lookup the
// Reseller filter needs.
export const fetchResellerOptions: any = createAsyncThunk(
  'resellerFilter/fetchResellerOptions',
  async () => {
    const timeZone = localStorage.getItem(SELECTED_TIME_ZONE);
    let skip = 0;
    let total = Infinity;
    const records: any[] = [];

    while (skip < total) {
      const response = await Api().get(
        `${RESELLER_FILTER.RESELLER_LIST_API}?take=${PAGE_SIZE}&skip=${skip}&TimeZone=${timeZone}`,
      );
      const pageRecords = response?.data?.records || [];
      records.push(...pageRecords);
      total = response?.data?.total_count ?? records.length;
      skip += PAGE_SIZE;
      if (pageRecords.length === 0) break;
    }

    return records;
  },
);

// Fetches every merchant referred by the given resellers (paginating each
// reseller until total_count is covered) and returns the deduped union.
export const fetchReferredMerchantsForResellers: any = createAsyncThunk(
  'resellerFilter/fetchReferredMerchantsForResellers',
  async (referralCodes: string[]) => {
    const timeZone = localStorage.getItem(SELECTED_TIME_ZONE);
    const merchantsByLegalName = new Map<string, any>();

    for (const referralCode of referralCodes) {
      let skip = 0;
      let total = Infinity;

      while (skip < total) {
        const response = await Api().get(
          `${RESELLER_FILTER.REFERRAL_MERCHANT_DETAILS}${referralCode}/?take=${PAGE_SIZE}&skip=${skip}&TimeZone=${timeZone}`,
        );
        const pageRecords = response?.data?.records || [];
        pageRecords.forEach((merchant: any) => {
          if (merchant?.LegalName) {
            merchantsByLegalName.set(merchant.LegalName, merchant);
          }
        });
        total = response?.data?.total_count ?? (skip + pageRecords.length);
        skip += PAGE_SIZE;
        if (pageRecords.length === 0) break;
      }
    }

    return Array.from(merchantsByLegalName.values());
  },
);

const initialState: any = {
  resellerOptions: [],
  resellerLegalNameToReferralCode: {},
  resellerLegalNameToReferralCount: {},
  loadingResellerOptions: false,
  referredMerchants: [],
  loadingReferredMerchants: false,
};

export const resellerFilterSlice = createSlice({
  name: 'resellerFilter',
  initialState,
  reducers: {
    resetReferredMerchants: state => {
      state.referredMerchants = [];
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchResellerOptions.pending, state => {
      state.loadingResellerOptions = true;
    });
    builder.addCase(fetchResellerOptions.fulfilled, (state, action) => {
      state.loadingResellerOptions = false;
      const records = action.payload || [];
      const legalNames = records
        .map((reseller: any) => reseller.legalName)
        .filter(Boolean);
      state.resellerOptions = customSort([...legalNames]);
      state.resellerLegalNameToReferralCode = records.reduce(
        (map: Record<string, string>, reseller: any) => {
          if (reseller.legalName) {
            map[reseller.legalName] = reseller.referralCode;
          }
          return map;
        },
        {},
      );
      // referralCount lets us skip fetching get-referred-merchant entirely
      // for resellers that refer nobody, and lets the filter recognise a
      // genuine "zero referred merchants" selection instead of confusing it
      // with "no reseller selected".
      state.resellerLegalNameToReferralCount = records.reduce(
        (map: Record<string, number>, reseller: any) => {
          if (reseller.legalName) {
            map[reseller.legalName] = reseller.referralCount || 0;
          }
          return map;
        },
        {},
      );
    });
    builder.addCase(fetchResellerOptions.rejected, state => {
      state.loadingResellerOptions = false;
    });

    builder.addCase(fetchReferredMerchantsForResellers.pending, state => {
      state.loadingReferredMerchants = true;
    });
    builder.addCase(fetchReferredMerchantsForResellers.fulfilled, (state, action) => {
      state.loadingReferredMerchants = false;
      state.referredMerchants = action.payload || [];
    });
    builder.addCase(fetchReferredMerchantsForResellers.rejected, state => {
      state.loadingReferredMerchants = false;
    });
  },
});

export const { resetReferredMerchants } = resellerFilterSlice.actions;

export default resellerFilterSlice.reducer;
